import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import "../../../styles/pages/submissionTracker.css";

// Maps FA icon name from API → FA class string
// Make sure Font Awesome 6 Free is loaded in your project
const FA_ICON_MAP = {
  "fa-draft":           "fa-solid fa-pen-to-square",
  "fa-submitted":       "fa-solid fa-paper-plane",
  "fa-reviewed":        "fa-solid fa-circle-check",
  "fa-approved":        "fa-solid fa-check-double",
  "fa-locked":          "fa-solid fa-lock",
  "fa-notstarted":      "fa-solid fa-clock",
  "fa-rework":          "fa-solid fa-rotate-left",
  "fa-list":            "fa-solid fa-list",
  "fa-exclamation":     "fa-solid fa-circle-exclamation",
  "fa-paperclip":       "fa-solid fa-paperclip",
};

const SubmissionTracker = () => {
  const navigate                           = useNavigate();
  const location                           = useLocation();
  const { reportingYear, reportingYearId } = useReportingYear();

  const [selectedEntity,  setSelectedEntity]  = useState("");
  const [legalEntities,   setLegalEntities]   = useState([]);
  const [statuses,        setStatuses]        = useState([]);
  const [overallProgress, setOverallProgress] = useState(0);
  const [modules,         setModules]         = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [filterStatus,    setFilterStatus]    = useState("");

  // 1. Fetch legal entities
  useEffect(() => {
    if (!reportingYearId) return;
    apiClient
      .get("/api/legal-entities-by-year", {
        params: { reporting_year_id: reportingYearId },
      })
      .then((res) => {
        setLegalEntities(res.data.code === 200 ? res.data.data || [] : []);
      })
      .catch(() => setLegalEntities([]));
  }, [reportingYearId]);

  // 2. Reset when year changes
  useEffect(() => {
    setSelectedEntity("");
    setStatuses([]);
    setModules([]);
    setOverallProgress(0);
    setFilterStatus("");
  }, [reportingYearId]);

  // 3. Restore entity when navigating back
  useEffect(() => {
    const incoming = location.state?.selectedEntity;
    if (incoming) {
      setSelectedEntity(String(incoming));
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, navigate, location.pathname]);

  // 4. Fetch status counts
  const fetchStatusCounts = useCallback(async () => {
    if (!reportingYearId || !selectedEntity) return;
    try {
      const res = await apiClient.get("/api/submission-status", {
        params: {
          reporting_year_id: reportingYearId,
          legal_entity_id:   selectedEntity,
        },
      });
      if (res.data?.success) setStatuses(res.data.data || []);
    } catch {
      setStatuses([]);
    }
  }, [reportingYearId, selectedEntity]);

const selectedEntityName = legalEntities.find(
    (e) => e.entity_id == selectedEntity
  )?.entity_name;

  // 5. Fetch table data
  const fetchTableData = useCallback(async () => {
    if (!reportingYearId || !selectedEntity) return;
    setLoading(true);
    try {
      const res = await apiClient.get("/api/submissions-tracker", {
        params: {
          reporting_year_id: reportingYearId,
          legal_entity_id:   selectedEntity,
          status:            filterStatus || undefined,
        },
      });
      setModules(res.data?.data || []);
      setOverallProgress(res.data?.overall_progress || 0);
    } catch {
      setModules([]);
      setOverallProgress(0);
    } finally {
      setLoading(false);
    }
  }, [reportingYearId, selectedEntity, filterStatus]);

  useEffect(() => {
    if (selectedEntity) {
      fetchStatusCounts();
      fetchTableData();
    }
  }, [fetchStatusCounts, fetchTableData, selectedEntity]);

  const entitySelected = !!selectedEntity;

  const buildReviewState = (extras = {}) => ({
    selectedEntity,
    legalEntities,
    reportingYearId,
    reportingYear,
    ...extras,
  });

  return (
    <div className="dash-page">
      <div className="dash-page-title">Submission Tracker</div>
      <div className="dash-page-subtitle">
        Track all module submissions through the approval workflow
      </div>

      {/* Filter card */}
      <div className="filter-card st-filter-card">
        <div className="filter-group">
          <label>Reporting Year</label>
          <input className="filter-input" value={reportingYear || ""} disabled />
        </div>

        <div className="filter-group">
          <label>Legal Entity *</label>
          <select
            className="filter-input"
            value={selectedEntity}
            onChange={(e) => {
              setSelectedEntity(e.target.value);
              setFilterStatus("");
              setModules([]);
              setStatuses([]);
              setOverallProgress(0);
            }}
          >
            <option value="">Select Legal Entity</option>
            {legalEntities.map((e) => (
              <option key={e.entity_id} value={e.entity_id}>
                {e.entity_name}
              </option>
            ))}
          </select>
        </div>

        <div className={`filter-group st-progress-group${entitySelected ? "" : " st-progress-group--hidden"}`}>
          <label>Overall Progress</label>
          <div className="st-overall-wrap">
            <div className="st-overall-track">
              <div
                className="st-overall-fill"
                style={{ "--overall-width": `${overallProgress}%` }}
              />
            </div>
            <span className="st-overall-pct">{overallProgress}%</span>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {!entitySelected && (
        <div className="st-empty-entity">
          <div className="st-empty-entity__icon">
            <i className="fa-solid fa-building" />
          </div>
          <div className="st-empty-entity__title">Select a Legal Entity to continue</div>
          <div className="st-empty-entity__sub">
            Choose a legal entity from the filter above to view submission status, progress and take actions.
          </div>
        </div>
      )}

      {/* Main content */}
      {entitySelected && (
        <>
          {/* Status stat cards */}
          <div className="stats-row">
            {statuses.map((s) => {
              const isActive = filterStatus === s.status_name;
              const faClass  = `fa-solid ${s.icon_name || "fa-circle"}`;
              return (
                <div
                  key={s.id}
                  className={`stat-card${isActive ? " st-stat-card--active" : ""}`}
                  style={{
                    "--card-bg":     s.color_code,
                    "--card-fg":     s.fore_color,
                    "--card-border": isActive ? s.fore_color : "transparent",
                  }}
                  onClick={() =>
                    setFilterStatus((p) => (p === s.status_name ? "" : s.status_name))
                  }
                >
                  {/* Icon box — background tinted from API color */}
                  <div
                    className="stat-icon"
                    style={{
                      "--icon-bg": s.color_code,
                      "--icon-fg": s.fore_color,
                      "background":s.color_code,
                    }}
                  >
                    <i className={faClass} />
                  </div>
                  <div className="st-stat-body">
                    <div className="stat-title">{s.status_name}</div>
                    <div className="stat-value">{s.count ?? 0}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Table card */}
          <div className="table-card">
            

            <table className="le-table">
              <thead>
                <tr>
                  <th>PRINCIPLE</th>
                  <th>DESCRIPTION</th>
                  <th>SUBMITTED BY</th>
                  <th>SUBMITTED ON</th>
                  <th>STATUS</th>
                  <th>REVIEWER</th>
                  <th>PROGRESS</th>
                  <th>LAST UPDATED</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="st-loading-row">Loading…</td>
                  </tr>
                ) : modules.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="st-empty-row">No data found.</td>
                  </tr>
                ) : (
                  modules.map((m) => {
                    const answered = Number(m.answered_count ?? 0);
                    const total    = Number(m.total_questions ?? 1);
                    const pct      = total > 0 ? Math.round((answered / total) * 100) : 0;

                    return (
                      <tr key={m.principle_id}>
                        <td className="st-principle-code">{m.principle_code}</td>
                        <td className="question-text">{m.principle_name}</td>
                        <td className="st-cell-muted">
                          {m.submitted_by_name && m.submitted_by_name !== "-"
                            ? m.submitted_by_name : "—"}
                        </td>
                        <td className="st-date-cell">
                          {m.submitted_action_date}
                            
                        </td>

                        {/* Status badge — FA icon from API + colors from API */}
                        <td>
                          <span
                            className="st-badge"
                            style={{
                              "--badge-bg": m.color_code,
                              "--badge-fg": m.fore_color,
                            }}
                          >
                            {m.icon_name && (
                             
                              <i className={`fa-solid ${m.icon_name ?? "fa-solid fa-circle"} status_icon_size`} />
                            )}
                            {m.status_display_name}
                          </span>
                        </td>

                        <td className="st-cell-muted">
                          {m.reviewed_by_name && m.reviewed_by_name !== "-"
                            ? m.reviewed_by_name : "—"}
                        </td>

                        {/* Progress bar */}
                        <td>
                          <div className="st-progress-row">
                            <div className="st-progress-bar">
                              <div
                                className="st-progress-fill"
                                style={{
                                  "--bar-color": m.progress_color ?? "#0f7aa8",
                                  "--bar-width": `${pct}%`,
                                }}
                              />
                            </div>
                            <span className="st-progress-text">{answered}/{total}</span>
                          </div>
                        </td>

                        <td className="st-date-cell">
                         {m.last_action_date}
                          
                        </td>

                        {/* Action link */}
                        <td>
                        <div className="action">
                          {m.action_lable === "N/A" || !m.action_lable ? (
                            <span className="action-link">—</span>
                          ) : (
                            <span
                              className="action-link"
                              onClick={() => {
                                navigate(m.action_url, {
                                  state: buildReviewState({
                                    principle_id:  m.principle_id,
                                    submission_id: m.submission_id ?? m.id,
                                    section_name:  m.principle_name,
                                    entityName: selectedEntityName,
                                    sourcePage: "submission-tracker" 
                                  }),
                                });
                              }}
                            >
                              {m.action_lable} →
                            </span>
                          )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Proceed to Review */}
          <div className="ecf-footer">
            <button
              className="st-proceed-btn"
              onClick={() =>
                navigate("/dashboard/review-screen", {
                  state: buildReviewState(),
                })
              }
            >
              Proceed to Review →
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SubmissionTracker;